//* Hooks Imports
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

//* Libraries Imports
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl as Input, InputGroup } from "react-bootstrap";

//* Imports Components
import DefaultButton from "../../../component/base/DefaultButton";

//* Imports Schemas
import { 
  sendEmailRecoveryPasswordSchema,
  type SendEmailRecoveryPasswordRequest 
} from "../../../schemas/common/recovery-password/recorvery-password";

//* Imports Services & Utils
import { useSendEmailRecoveryPassword } from "../../../hooks/mutations/recovery-password/use-send-email-recovery-password";

type SendEmailRecoveryPasswordFormData = SendEmailRecoveryPasswordRequest;

export function SendEmailRecoveryPassword() {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SendEmailRecoveryPasswordFormData>({
    resolver: zodResolver(sendEmailRecoveryPasswordSchema),
    defaultValues: {
      cpf: "",
    },
  });

  const sendEmailMutation = useSendEmailRecoveryPassword();
  const cpfValue = watch("cpf");

  // Format CPF input
  const handleCpfChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value.replace(/\D/g, "");
    
    let formattedValue = rawValue;
    if (rawValue.length <= 11) {
      formattedValue = rawValue
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d)/, "$1.$2")
        .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
    }

    setValue("cpf", formattedValue, { shouldValidate: true });
  };

  const onSubmit = async (data: SendEmailRecoveryPasswordFormData) => {
    setErrorMessage("");
    setSuccessMessage("");

    try {
      await sendEmailMutation.mutateAsync(data);
      setSuccessMessage("Email de recuperação enviado com sucesso! Verifique sua caixa de entrada.");
    } catch (error: any) {
      setErrorMessage(error.message || "Erro ao enviar email de recuperação. Tente novamente.");
    }
  };
  return (
    <>
      <h4 className="font-18 m-b-5 text-center header-title" style={{ fontSize: 18 }}>
        Recuperar Senha
      </h4>
      <p className="text-muted text-center mb-4" style={{ fontSize: 14 }}>
        Digite seu CPF para receber o link de recuperação
      </p>

      <form onSubmit={handleSubmit(onSubmit)}>
        <InputGroup className="mb-3">
          <div className="flex fdcolumn flex1 w-100">
            <Form.Label>
              CPF
            </Form.Label>
            <Input
              type="text"
              className="form-control fs-6"
              placeholder="000.000.000-00"
              {...register("cpf")}
              onChange={handleCpfChange}
              value={cpfValue}
              autoComplete="username"
              maxLength={14}
              disabled={sendEmailMutation.isPending}
            />
            {errors.cpf && (
              <span className="text-danger small mt-1">
                {errors.cpf.message}
              </span>
            )}
          </div>
        </InputGroup>

        {/* Success Message */}
        {successMessage && (
          <div className="alert alert-success small mb-3" role="alert">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="alert alert-danger small mb-3" role="alert">
            {errorMessage}
          </div>
        )}

        <div className="form-group row mt-4 mb-3">
          <div className="col-sm-12 text-end">
            <DefaultButton
              type="submit"
              bg="primary"
              width="100%"
              height="3rem"
              className="mt-2"
              text="Enviar Email"
              loadingtext="Enviando..."
              loading={sendEmailMutation.isPending}
              disabled={sendEmailMutation.isPending}
            />
          </div>
        </div>

        {/* Voltar ao login */}
        <div className="text-center">
          <Link
            to="/login"
            className="small fw-bold text-primary text-decoration-none"
          >
            Voltar ao Login
          </Link>
        </div>
      </form>
    </>
  );
}